import type { Identifier } from "ra-core";
import {
  useGetIdentity,
  useListContext,
  useTimeout,
  useTranslate,
} from "ra-core";
import { Link } from "react-router";

import { RelativeDate } from "../misc/RelativeDate";
import { Status } from "../misc/Status";
import { useGetSalesName } from "../sales/useGetSalesName";
import type { ContactNote } from "../types";
import { InfinitePagination } from "../misc/InfinitePagination";
import { ListCardsSkeleton, ListErrorState } from "../design/ListStates";

export const NotesIteratorMobile = ({
  contactId,
  showStatus,
}: {
  contactId: Identifier;
  showStatus?: boolean;
}) => {
  const {
    data = [],
    error,
    isPending,
    refetch,
  } = useListContext<ContactNote>();
  const translate = useTranslate();
  const oneSecondHasPassed = useTimeout(1000);
  if (isPending) {
    if (!oneSecondHasPassed) {
      return null;
    }
    return <ListCardsSkeleton rows={3} />;
  }
  if (error && !data?.length) {
    return (
      <ListErrorState
        message={translate("resources.notes.list.error_loading", {
          _: "Error loading notes",
        })}
        onRetry={() => refetch()}
      />
    );
  }

  return (
    <>
      <div className="divide-y">
        {data.map((note) => (
          <NoteMobile
            key={note.id}
            note={note}
            contactId={contactId}
            showStatus={showStatus}
          />
        ))}
      </div>
      <InfinitePagination />
    </>
  );
};

export const NoteMobile = ({
  note,
  contactId,
  showStatus,
}: {
  note: ContactNote;
  contactId: Identifier;
  showStatus?: boolean;
}) => {
  const translate = useTranslate();
  const { identity } = useGetIdentity();
  const isCurrentUser = note.sales_id === identity?.id;
  const salesName = useGetSalesName(note.sales_id, {
    enabled: !isCurrentUser,
  });

  return (
    <Link
      to={`/contacts/${contactId}/notes/${note.id}`}
      className="block min-h-11 active:bg-accent/50 -mx-2 px-2 py-2 rounded-md"
    >
      <div className="flex items-center space-x-2 w-full">
        <div className="inline-flex h-full items-center text-sm text-muted-foreground">
          {isCurrentUser ? translate("resources.notes.me") : salesName}{" "}
          {showStatus && note.status && (
            <Status
              className="ml-2"
              status={note.status}
              labelClassName="text-[12.5px]"
            />
          )}
        </div>
        <div className="flex-1" />
        <span className="text-sm text-muted-foreground tabular-nums">
          <RelativeDate date={note.date} />
        </span>
      </div>
      {note.text && (
        <p className="pt-2 text-sm line-clamp-3">
          {note.text.replace(/\s+/g, " ").trim()}
        </p>
      )}
    </Link>
  );
};
